import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  CASH_FLOW_TABS,
  cashFlowInitialValues,
} from 'components/organisms/forms/cash-flow/CashFlowForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostCost, usePutCostFile } from 'services/cash-flow/cost';
import { ICashFlowForm } from 'services/cash-flow/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const CreateCost = () => {
  const labelId = useCustomParamId('id', '/label');

  const { pathname, state: locationState } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const createCost = usePostCost();

  const mutateOptions = useMutateOptions<ICashFlowForm>();

  const addFile = usePutCostFile();

  const onGoToDetail = (id: string) => {
    const lastPath = getLastPathname(pathname);
    const tab = lastPath === 'create' ? 'file' : lastPath;

    navigate(`/label/${labelId}/cost/${id}/${tab}`, { state: locationState });
  };

  const addFileToCost = async (file: File, id: string) => {
    await addFile.mutateAsync(
      { file, id },
      {
        onSuccess: () => onGoToDetail(id),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onCreateCost = async (
    values: ICashFlowForm,
    helpers: FormikHelpers<ICashFlowForm>
  ) => {
    await createCost.mutateAsync(
      { ...middleware.cost.formToQuery(values), labelId: labelId },
      {
        ...mutateOptions(helpers),
        onSuccess: id =>
          values.file ? addFileToCost(values.file, id) : onGoToDetail(id),
      }
    );
  };

  return (
    <Form.CashFlow
      initialValues={{
        ...cashFlowInitialValues,
        repaymentsFromAllContracts: true,
      }}
      onSubmit={onCreateCost}
      disableTabs={[CASH_FLOW_TABS.repayments]}
      customBackItem={
        <Button onlyIcon onClick={() => navigate(`/label/${labelId}/balance`)}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.create')} />
        </Button>
      }
    />
  );
};

export default CreateCost;
